import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String }
  static targets = ["input", "dropdown", "results"]

  connect() {
    this.delimiter = ", "
    this.minLength = 2
    this.currentFocus = -1
    this.isOpen = false
    
    // Create dropdown if it doesn't exist
    if (!this.hasDropdownTarget) {
      this.createDropdown()
    }
    
    // Bind event listeners
    this.inputTarget.addEventListener("input", this.handleInput.bind(this))
    this.inputTarget.addEventListener("keydown", this.handleKeydown.bind(this))
    this.inputTarget.addEventListener("focus", this.handleFocus.bind(this))
    document.addEventListener("click", this.handleDocumentClick.bind(this))
  }

  disconnect() {
    document.removeEventListener("click", this.handleDocumentClick.bind(this))
  }

  createDropdown() {
    const dropdown = document.createElement("div")
    dropdown.className = "autocomplete-dropdown position-absolute w-100 bg-white border border-secondary rounded shadow-sm"
    dropdown.style.cssText = "z-index: 1000; max-height: 200px; overflow-y: auto; display: none;"
    dropdown.setAttribute("data-autocomplete-target", "dropdown")
    
    const results = document.createElement("ul")
    results.className = "list-unstyled m-0 p-0"
    results.setAttribute("data-autocomplete-target", "results")
    
    dropdown.appendChild(results)
    this.element.style.position = "relative"
    this.element.appendChild(dropdown)
  }

  handleInput(event) {
    const query = this.getCurrentQuery()
    
    if (query.length >= this.minLength) {
      this.search(query)
    } else {
      this.hideDropdown()
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) return
    
    const items = this.resultsTarget.querySelectorAll(".autocomplete-item")
    
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.currentFocus = Math.min(this.currentFocus + 1, items.length - 1)
        this.updateActiveItem(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.currentFocus = Math.max(this.currentFocus - 1, -1)
        this.updateActiveItem(items)
        break
      case "Enter":
        event.preventDefault()
        if (this.currentFocus >= 0 && items[this.currentFocus]) {
          this.selectItem(items[this.currentFocus].textContent)
        }
        break
      case "Escape":
        this.hideDropdown()
        break
    }
  }

  handleFocus(event) {
    const query = this.getCurrentQuery()
    if (query.length >= this.minLength) {
      this.search(query)
    }
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  async search(query) {
    if (!this.urlValue) return
    
    try {
      this.showLoading()
      
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("term", query)
      
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      
      if (!response.ok) throw new Error("Network response was not ok")
      
      const results = await response.json()
      this.displayResults(results)
    } catch (error) {
      console.error("Autocomplete search failed:", error)
      this.hideDropdown()
    }
  }

  showLoading() {
    this.resultsTarget.innerHTML = '<li class="autocomplete-item p-2 text-muted">Searching...</li>'
    this.showDropdown()
  }

  displayResults(results) {
    this.resultsTarget.innerHTML = ""
    this.currentFocus = -1
    
    if (results.length === 0) {
      this.resultsTarget.innerHTML = '<li class="autocomplete-item p-2 text-muted">No results found</li>'
    } else {
      results.forEach(result => {
        const item = document.createElement("li")
        item.className = "autocomplete-item p-2 cursor-pointer"
        item.textContent = result
        item.addEventListener("click", () => this.selectItem(result))
        item.addEventListener("mouseenter", () => {
          this.currentFocus = Array.from(this.resultsTarget.children).indexOf(item)
          this.updateActiveItem(this.resultsTarget.querySelectorAll(".autocomplete-item"))
        })
        this.resultsTarget.appendChild(item)
      })
    }
    
    this.showDropdown()
  }

  selectItem(value) {
    const currentValue = this.inputTarget.value
    const parts = currentValue.split(this.delimiter)
    const currentQuery = this.getCurrentQuery()
    
    // Replace the current query with the selected value
    const lastCommaIndex = currentValue.lastIndexOf(currentQuery)
    if (lastCommaIndex !== -1) {
      const beforeQuery = currentValue.substring(0, lastCommaIndex)
      const afterQuery = currentValue.substring(lastCommaIndex + currentQuery.length)
      
      let newValue = beforeQuery + value
      
      // Add delimiter if there are more parts or if we're not at the end
      if (afterQuery.trim() || (beforeQuery.trim() && !beforeQuery.endsWith(this.delimiter.trim()))) {
        newValue += this.delimiter
      }
      
      newValue += afterQuery
      this.inputTarget.value = newValue.trim()
    } else {
      // Fallback: replace entire value
      this.inputTarget.value = value
    }
    
    this.hideDropdown()
    this.inputTarget.focus()
    
    // Trigger input event to notify other controllers
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  getCurrentQuery() {
    const value = this.inputTarget.value
    const cursorPos = this.inputTarget.selectionStart
    const beforeCursor = value.substring(0, cursorPos)
    const parts = beforeCursor.split(this.delimiter)
    return parts[parts.length - 1].trim()
  }

  updateActiveItem(items) {
    items.forEach((item, index) => {
      item.classList.toggle("active", index === this.currentFocus)
      item.classList.toggle("bg-light", index === this.currentFocus)
    })
  }

  showDropdown() {
    this.dropdownTarget.style.display = "block"
    this.isOpen = true
  }

  hideDropdown() {
    this.dropdownTarget.style.display = "none"
    this.isOpen = false
    this.currentFocus = -1
  }
}